/**
 * Eggs Fighting Game — public API
 *
 * Mount <EggsFightingGame /> anywhere. Pass two players and callbacks.
 * The game auto-starts. No external dependencies required.
 */

import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import EggsFightingGame from "./components/EggsFightingGame";
import { GameResult, Player } from "./types";
import { getRandomKey } from "./utils/gameLogic";

// Main component

// Sub-components (for Storybook / custom layouts)
export { ArenaScreen } from "./components/ArenaScreen";
export { WinScreen } from "./components/WinScreen";
export { LoseScreen } from "./components/LoseScreen";
export { TieScreen } from "./components/TieScreen";
export { EggDisplay } from "./components/EggDisplay";
export { CountdownOverlay } from "./components/CountdownOverlay";
export { ReadyScreen } from "./components/ReadyScreen";
export { ImpactEffect } from "./components/ImpactEffect";
export { RoundResultOverlay } from "./components/RoundResultOverlay";
export { GrassStrip } from "./components/GrassStrip";

// Config — for external consumers who want to override values
export { GAME_CONFIG } from "./config/gameConfig";

// Types
export type {
  Player,
  PlayerRole,
  GamePhase,
  GameState,
  GameResult,
  RoundResult,
  EggsFightingGameProps,
  EggSide,
} from "./types";

// Utilities — for testing and Storybook
export {
  resolveRound,
  assignRoles,
  determineMatchWinner,
} from "./utils/gameLogic";

export { UI_STRINGS } from "./strings/uiStrings";

export default () => {
  const [running, setRunning] = useState(true);
  const [readyCount, setReadyCount] = useState(0);
  const [predefinedWinner, setPredefinedWinner] = useState<string | null>(null);
  const randomKey = useMemo(() => getRandomKey(), []);

  useEffect(() => {
    window.EventManager?.addHandler("start:egg:fight", (winner: string) => {
      setPredefinedWinner(winner ?? null);
      setRunning(true);
    });

    return () => {
      window.EventManager?.removeHandler("start:egg:fight");
    };
  }, []);

  return (
    <AnimatePresence mode="wait">
      {running ? (
        <EggsFightingGame
          key="game"
          player1={{ id: "p1", name: "Megu" }}
          player2={{ id: "p2", name: "Heyz" }}
          readyKey={randomKey}
          readyCount={readyCount}
          predefinedWinner={predefinedWinner}
          onPlayerReady={(player: Player) => {
            console.log(`${player.name} is ready!`);
            setReadyCount((prev) => prev + 1); // ← OVO pali countdown kada dođe do 2
          }}
          onGameEnd={(result: GameResult) => {
            console.log(result);
            setRunning(false);
          }}
          onClose={() => setRunning(false)}
        />
      ) : (
        <motion.div
          key="waiting"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="fixed inset-0 flex flex-col items-center justify-center gap-4"
          style={{ zIndex: 1000, background: "rgba(0,0,0,0.75)", pointerEvents: "none" }}
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              border: "3px solid rgba(255,255,255,0.12)",
              borderTopColor: "#1A8BF7",
            }}
          />
          <span className="there-brat-font text-white/60 text-sm tracking-[0.2em] uppercase">
            Waiting for match…
          </span>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
